import React, { useState } from 'react';
import './Table.css';

/**
 * Table Component
 * Reusable data table with sorting, actions, and empty states
 * 
 * @param {Array} columns - Column definitions [{ key, label, sortable, render }]
 * @param {Array} data - Table data array
 * @param {Array} actions - Action buttons [{ icon, label, onClick, disabled }]
 * @param {string} emptyMessage - Message when no data
 * @param {boolean} loading - Loading state
 * @param {function} onSort - Sort handler (optional external sorting)
 */
const Table = ({ 
  columns, 
  data, 
  actions, 
  emptyMessage = 'No data available',
  loading = false,
  onSort = null
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // Internal sorting if no external handler
  const sortedData = React.useMemo(() => {
    if (!sortField || onSort) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortOrder, onSort]);

  const handleSort = (key) => {
    if (onSort) {
      onSort(key, sortField === key && sortOrder === 'asc' ? 'desc' : 'asc');
    }
    if (sortField === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(key);
      setSortOrder('asc');
    }
  };

  if (loading) {
    return (
      <div className="table-loading">
        <div className="spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="table-empty">
        <i className="fas fa-inbox"></i>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table className="minimal-table">
        <thead>
          <tr>
            {columns.map((col, idx) => (
              <th 
                key={idx}
                className={col.sortable ? 'sortable-header' : ''}
                onClick={() => col.sortable && handleSort(col.key)}
              >
                {col.label}
                {col.sortable && sortField === col.key && (
                  <i className={`fas fa-arrow-up sort-icon ${sortOrder === 'asc' ? 'asc' : 'desc'}`}></i>
                )}
                {col.sortable && sortField !== col.key && (
                  <i className="fas fa-sort sort-icon"></i>
                )}
              </th>
            ))}
            {actions && actions.length > 0 && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {(onSort ? data : sortedData).map((row, rowIdx) => (
            <tr key={row._id || row.id || rowIdx}>
              {columns.map((col, colIdx) => (
                <td key={colIdx}>
                  {col.render ? col.render(row[col.key], row) : row[col.key]}
                </td>
              ))}
              {actions && actions.length > 0 && (
                <td className="actions-cell">
                  {actions.map((action, actionIdx) => {
                    const isDisabled = action.disabled ? action.disabled(row) : false;
                    return (
                      <button
                        key={actionIdx}
                        className="action-btn"
                        onClick={() => !isDisabled && action.onClick(row)}
                        title={action.label}
                        disabled={isDisabled}
                      >
                        <i className={`fas ${action.icon}`}></i>
                      </button>
                    );
                  })}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;